import { CalendarDays, Clock, MapPin, Sparkles, CalendarPlus } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import { Countdown } from "./Countdown";
import content from "../content";

const pill =
  "inline-flex items-center gap-2 border border-white/15 bg-white/5 backdrop-blur-sm px-4 py-2 font-poppins text-white text-sm";

function Banner({ c }) {
  const t = `countdown-${c.id}`;
  return (
    <div className="relative w-full max-w-7xl mx-auto px-6 mb-10 md:mb-14 last:mb-16 md:last:mb-24" data-testid={t}>
      <Reveal>
        <Rise>
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
            <img
              src={c.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/80 to-ink/50" />
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: "radial-gradient(circle at 15% 10%, rgba(33,112,232,0.28), transparent 55%)" }}
              aria-hidden="true"
            />

            <div className="relative grid lg:grid-cols-[1fr_auto] gap-10 items-center p-6 sm:p-10 md:p-14">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-electric/40 bg-electric/10 px-4 py-2 mb-6">
                  <Sparkles size={14} className="text-electric" />
                  <span className="font-poppins text-[11px] md:text-xs tracking-[0.1em] sm:tracking-[0.22em] uppercase text-electric font-semibold">
                    {c.eyebrow}
                  </span>
                </span>
                <h3
                  className="display text-white text-[clamp(2.25rem,6vw,4.5rem)] leading-[0.92] mb-5"
                  data-testid={`${t}-title`}
                >
                  {c.title}
                </h3>
                {c.note && (
                  <p className="max-w-xl font-poppins text-ceramic/85 text-sm md:text-base mb-6" data-testid={`${t}-note`}>
                    {c.note}
                  </p>
                )}
                <div className="flex flex-wrap gap-2.5 mb-8">
                  <span className={`${pill} rounded-full`} data-testid={`${t}-date`}>
                    <CalendarDays size={15} className="text-electric shrink-0" /> {c.dateLabel}
                  </span>
                  <span className={`${pill} rounded-full`} data-testid={`${t}-time`}>
                    <Clock size={15} className="text-electric shrink-0" /> {c.timeLabel}
                  </span>
                  <span className={`${pill} rounded-2xl sm:rounded-full`} data-testid={`${t}-location`}>
                    <MapPin size={15} className="text-electric shrink-0" /> {c.location}
                  </span>
                </div>

                <Countdown targetISO={c.startISO} liveLabel={c.liveLabel} testId={`${t}-timer`} compact />

                {c.calendarHref && (
                  <a
                    href={c.calendarHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`${t}-calendar`}
                    className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-white text-ink px-7 py-3.5 font-poppins font-semibold text-sm uppercase tracking-wide hover:bg-electric hover:text-white transition-colors duration-300"
                  >
                    <CalendarPlus size={18} />
                    {c.calendarLabel}
                  </a>
                )}
              </div>

              <img
                src={c.image}
                alt={c.imageAlt}
                loading="lazy"
                className="hidden lg:block w-[300px] xl:w-[340px] aspect-[4/5] object-cover rounded-2xl border border-white/10 shadow-2xl rotate-[2deg] transition-transform duration-500 hover:rotate-0"
                data-testid={`${t}-flyer`}
              />
            </div>
          </div>
        </Rise>
      </Reveal>
    </div>
  );
}

export default function CountdownBanners() {
  const now = Date.now();
  const list = (content.esteMes?.countdowns || [])
    .filter((c) => !c.hidden && !(c.endISO && now > new Date(c.endISO).getTime()))
    .sort((a, b) => new Date(a.startISO) - new Date(b.startISO));
  if (!list.length) return null;
  return (
    <div data-testid="countdown-banners">
      {list.map((c) => (
        <Banner key={c.id} c={c} />
      ))}
    </div>
  );
}
